// A glossary word's definition, over the page. One for the whole app, with its
// listeners on `document` the way the context menu's are: a word can sit in
// markup React never sees — the ESV's, which `Passage` fills in by hand — so
// there is no component to hang a handler on.
//
// Hovering shows it and moving away hides it; a click, a tap or Enter pins it
// until the reader clicks elsewhere or presses Escape.
import { renderMarkdown } from "@bibleunleashed/shared";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { track } from "@/lib/events";
import { useGlossary } from "@/lib/glossary";

const TERM = ".term[data-term]";
const OPEN_DELAY = 250;
const CLOSE_DELAY = 200;

interface Shown {
  anchor: HTMLElement;
  pinned: boolean;
}

export function TermPopover() {
  const { data: entries } = useGlossary();
  const [shown, setShown] = useState<Shown | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  // The listeners are bound once; they read the current state through this.
  const current = useRef<Shown | null>(null);
  current.current = shown;
  const [location] = useLocation();

  useEffect(() => setShown(null), [location]);

  useEffect(() => {
    let timer: number | undefined;
    const after = (ms: number, fn: () => void) => {
      clearTimeout(timer);
      timer = window.setTimeout(fn, ms);
    };
    const termOf = (t: EventTarget | null) => (t instanceof Element ? t.closest<HTMLElement>(TERM) : null);
    const inBox = (t: EventTarget | null) => t instanceof Node && Boolean(box.current?.contains(t));
    const show = (anchor: HTMLElement, pinned: boolean) => {
      clearTimeout(timer);
      setShown({ anchor, pinned });
    };
    const hide = () => {
      clearTimeout(timer);
      setShown(null);
    };
    /** A click, a tap or a key: the second on the same word puts it away. */
    const toggle = (anchor: HTMLElement) => {
      const s = current.current;
      if (s?.anchor === anchor && s.pinned) hide();
      else show(anchor, true);
    };

    const onOver = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const term = termOf(e.target);
      if (inBox(e.target) || (term && term === current.current?.anchor)) return clearTimeout(timer);
      if (term && !current.current?.pinned) after(OPEN_DELAY, () => show(term, false));
    };
    const onOut = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || current.current?.pinned) return;
      if (!termOf(e.target) && !inBox(e.target)) return clearTimeout(timer);
      if (inBox(e.relatedTarget) || termOf(e.relatedTarget) === current.current?.anchor) return;
      after(CLOSE_DELAY, hide);
    };
    const onClick = (e: MouseEvent) => {
      if (inBox(e.target)) return;
      const term = termOf(e.target);
      if (term) toggle(term);
      else if (current.current) hide();
    };
    const onFocusIn = (e: FocusEvent) => {
      const term = termOf(e.target);
      if (term && current.current?.anchor !== term) show(term, false);
    };
    const onFocusOut = (e: FocusEvent) => {
      const s = current.current;
      if (s && !s.pinned && termOf(e.target) === s.anchor && !inBox(e.relatedTarget)) hide();
    };
    const onKey = (e: KeyboardEvent) => {
      const s = current.current;
      if (e.key === "Escape" && s) {
        hide();
        s.anchor.focus();
        return;
      }
      const term = termOf(e.target);
      if (term && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        toggle(term);
      }
    };

    document.addEventListener("pointerover", onOver);
    document.addEventListener("pointerout", onOut);
    document.addEventListener("click", onClick);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", hide);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerover", onOver);
      document.removeEventListener("pointerout", onOut);
      document.removeEventListener("click", onClick);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", hide);
    };
  }, []);

  const entry = shown ? entries?.find((e) => e.id === shown.anchor.dataset.term) : undefined;

  // Counted when a word's definition appears, once for each time it does.
  const anchor = shown?.anchor;
  const entryId = entry?.id;
  useEffect(() => {
    if (anchor && entryId) track({ type: "glossary_term", detail: entryId });
  }, [anchor, entryId]);

  // Placed once it has been drawn, so its own height decides whether it fits
  // below the word or has to go above. In page coordinates, so it scrolls with
  // the word rather than floating free of it.
  useLayoutEffect(() => {
    const el = box.current;
    if (!shown || !el || !entry) return setPos(null);
    // A wrapped phrase has a box per line; the popover belongs to the first.
    const r = shown.anchor.getClientRects()[0] ?? shown.anchor.getBoundingClientRect();
    const gap = 6;
    const margin = 8;
    const fitsBelow = r.bottom + gap + el.offsetHeight <= window.innerHeight - margin;
    const top = fitsBelow || r.top - gap - el.offsetHeight < margin ? r.bottom + gap : r.top - gap - el.offsetHeight;
    const left = Math.max(margin, Math.min(r.left, window.innerWidth - el.offsetWidth - margin));
    setPos({ top: top + window.scrollY, left: left + window.scrollX });
  }, [shown, entry]);

  useEffect(() => {
    if (!shown || !entry) return;
    shown.anchor.setAttribute("aria-expanded", "true");
    shown.anchor.setAttribute("aria-controls", "term-popover");
    return () => {
      shown.anchor.removeAttribute("aria-expanded");
      shown.anchor.removeAttribute("aria-controls");
    };
  }, [shown, entry]);

  if (!shown || !entry || !shown.anchor.isConnected) return null;

  return createPortal(
    <div
      ref={box}
      id="term-popover"
      role="dialog"
      aria-label={`${entry.term}: glossary`}
      style={pos ?? { top: 0, left: 0, visibility: "hidden" }}
      className="absolute z-50 w-[min(340px,calc(100vw-16px))] rounded-lg border border-rule bg-paper px-4 py-3.5 shadow-pop"
    >
      <p className="font-serif text-[17px] font-semibold text-ink">{entry.term}</p>
      {entry.definitionMd.trim() ? (
        <div
          className="prose mt-1 font-serif text-[15px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.definitionMd) }}
        />
      ) : (
        <p className="mt-1 font-serif text-[15px] text-ink-3 italic">Not defined yet.</p>
      )}
      <Link href={`/glossary#${encodeURIComponent(entry.id)}`} className="mt-2.5 inline-block font-sans text-[12px] text-accent hover:underline">
        In the glossary
      </Link>
    </div>,
    document.body,
  );
}
